import React from "react";

import { MegaSectionButton } from "./MegaSectionButton";
import { MegaTab } from "./MegaTab";
import { MenuItem } from "./MenuItem";
import { Separator } from "./Separator";
import { T } from "./MegaMenu.tlocale";

export interface MegaItem {
  label: string;
  to: string;
}

export interface MegaItems {
  label: string;
  items: MegaItem[];
}

export interface MegaFeatured {
  imgSrc: string;
  title: string;
  subtitle: string;
  to: string;
}

export interface MegaSection {
  id: string;
  label: string;

  menus: MegaItems[];
  featured: MegaFeatured[];
}

export interface MegaState {
  visible: boolean;
  section: string;
  menu: number;
}

export interface MegaMenuProps {
  active: MegaState; // mega section id or empty string
  onActiveChange: (v: MegaState) => void;

  sections: MegaSection[];
  mobileMenuItems?: JSX.Element[];
}

export const MegaMenu = (props: MegaMenuProps) => {
  const sec = props.sections.find((s) => s.id === props.active.section);
  if (!sec) return null;
  const menu = sec.menus.find((_m, idx) => idx === props.active.menu);
  return (
    <>
      <div className="hidden p-12 tablet:flex gap-14 flex-wrap">
        {sec.menus.map((m, idx) => (
          <div key={idx} className="flex flex-col">
            <div className="txt-body-14sb text-ash uppercase">{m.label}</div>
            <div className="space-y-4 py-4">
              {m.items.map((item, idx) => (
                <MenuItem kind="link" key={idx} to={item.to} label={item.label} />
              ))}
            </div>
          </div>
        ))}
        {sec.featured.length > 0 ? (
          <div className="flex flex-col">
            <div className="txt-body-14sb text-ash">{T("FEATURED")}</div>
            {sec.featured.length > 0 ? (
              <div className="space-y-5 py-4">
                {sec.featured.map((f, idx) => (
                  <div
                    key={idx}
                    className="relative w-[433px] h-[164px] p-6 flex flex-col justify-end select-none cursor-pointer"
                  >
                    <img src={f.imgSrc} className="object-cover absolute w-full h-full inset-0" />
                    <div className="relative txt-heading-5b text-white">{f.title}</div>
                    <div className="relative txt-body-16 text-white">{f.subtitle}</div>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
      <div className="flex flex-col tablet:hidden px-4 pt-5 pb-10 gap-6">
        <div className="flex gap-3">
          {props.sections.map((s) => (
            <MegaTab
              key={s.id}
              onClick={() => {
                props.onActiveChange({ section: s.id, menu: -1, visible: true });
              }}
              active={s.id === props.active.section}
              label={s.label}
            />
          ))}
        </div>
        {!menu ? (
          <>
            <div className="flex flex-col">
              {sec.menus.map((m, idx) => (
                <React.Fragment key={idx}>
                  {idx !== 0 ? <Separator /> : null}
                  <MegaSectionButton
                    label={m.label}
                    onClick={() => {
                      props.onActiveChange({ section: props.active.section, menu: idx, visible: true });
                    }}
                  />
                </React.Fragment>
              ))}
            </div>
            {sec.featured.length > 0 ? (
              <div className="flex flex-col gap-3">
                {sec.featured.map((f, idx) => (
                  <div
                    key={idx}
                    className="relative h-[138px] p-6 flex flex-col justify-end select-none cursor-pointer"
                  >
                    <img src={f.imgSrc} className="object-cover absolute w-full h-full inset-0" />
                    <div className="relative txt-body-18sb text-white">{f.title}</div>
                    <div className="relative txt-body-14 text-white">{f.subtitle}</div>
                  </div>
                ))}
              </div>
            ) : null}
            {(props.mobileMenuItems?.length || 0) > 0 ? (
              <div className="grid grid-cols-2 items-center gap-y-4 gap-x-2">
                {props.mobileMenuItems?.map((m, idx) => <React.Fragment key={idx}>{m}</React.Fragment>)}
              </div>
            ) : null}
          </>
        ) : (
          <div className="flex flex-col">
            <MegaSectionButton
              label={menu.label}
              active
              onClick={() => {
                props.onActiveChange({ section: props.active.section, menu: -1, visible: true });
              }}
            />
            <div className="space-y-4 pt-1.5 pl-5">
              {menu.items.map((item, idx) => (
                <MenuItem kind="link" key={idx} to={item.to} label={item.label} />
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
};
